import React, { useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { useLocation } from 'react-router-dom';
import { DEFAULT_IMAGE, absoluteUrl, getRouteSeo, OG_LOCALE, SITE_NAME, siteSchemas } from '@/data/seo.js';

function Seo() {
  const { pathname } = useLocation();
  const seo = useMemo(() => getRouteSeo(pathname), [pathname]);
  const canonicalPath = seo.canonicalPath ?? pathname;
  const canonicalUrl = absoluteUrl(canonicalPath);
  const title = seo.title;
  const description = seo.description;
  const image = absoluteUrl(seo.image ?? DEFAULT_IMAGE);
  const robots = seo.noindex ? 'noindex,nofollow,max-image-preview:large' : 'index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1';
  const keywords = seo.keywords?.join(', ');
  const schemas = [...siteSchemas, ...(seo.schemas ?? [])];

  return (
    <Helmet>
      <html lang="es" />
      <title>{title}</title>
      <meta name="author" content="Lucila Pistiner" />
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <meta name="robots" content={robots} />
      <link rel="canonical" href={canonicalUrl} />
      <link rel="sitemap" type="application/xml" href="/sitemap.xml" />
      <link rel="alternate" type="text/plain" href="/llms.txt" title="LLMS.txt" />
      <link rel="alternate" type="text/plain" href="/llms-full.txt" title="LLMS full context" />

      <meta property="og:locale" content={OG_LOCALE} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:type" content={seo.type === 'article' ? 'article' : 'website'} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={image} />
      <meta property="og:image:alt" content={title} />
      {seo.publishedTime && <meta property="article:published_time" content={seo.publishedTime} />}
      {seo.modifiedTime && <meta property="article:modified_time" content={seo.modifiedTime} />}

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:image:alt" content={title} />

      {schemas.map((schema, index) => (
        <script key={`${canonicalPath}-schema-${index}`} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
}

export default Seo;
